'use client'

import { Address, AddressOwner, getAddresses } from '@/lib/api/addresses'
import { formatCEP } from '@/lib/cep'
import { useEffect, useState } from 'react'
import { MdStar } from 'react-icons/md'
import { formatAddressLine } from './address-fields'

interface AddressListProps {
    /** Exatamente um id deve ser informado. */
    owner: AddressOwner
    emptyHint?: string
}

/**
 * Exibição somente-leitura dos endereços de uma entidade, para telas de detalhe.
 *
 * Lê da tabela `Address` e não das colunas legadas da entidade. Essa distinção
 * importa: os formulários deixaram de escrever as colunas antigas, então uma
 * entidade criada depois dessa mudança apareceria SEM endereço numa tela que
 * ainda lesse o modelo antigo — e uma entidade antiga mostraria o valor
 * congelado, que desatualiza assim que alguém editar o endereço de verdade.
 */
export default function AddressList({ owner, emptyHint }: AddressListProps) {
    const [addresses, setAddresses] = useState<Address[]>([])
    const [loading, setLoading] = useState(true)

    const ownerKey = `${owner.partnerId || ''}|${owner.professionalId || ''}|${owner.hospitalId || ''}`

    useEffect(() => {
        let cancelled = false
        setLoading(true)
        getAddresses(owner)
            .then(response => {
                if (!cancelled && response.success) setAddresses(response.result)
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
        // `owner` é um objeto novo a cada render do pai; comparar pelos ids
        // evita refazer a busca sem necessidade.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ownerKey])

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-sm text-base-content/50">
                <span className="loading loading-spinner loading-xs" />
                Carregando endereços...
            </div>
        )
    }

    if (addresses.length === 0) {
        return (
            <p className="text-base-content/50 text-sm">
                {emptyHint || 'Nenhum endereço cadastrado'}
            </p>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            {addresses.map(address => (
                <div
                    key={address.id}
                    className="flex items-start gap-2 pb-3 last:pb-0 border-b last:border-b-0 border-base-300/40"
                >
                    {address.isPrimary && (
                        <MdStar
                            className="text-warning mt-0.5 flex-shrink-0"
                            size={16}
                            title="Endereço principal"
                        />
                    )}
                    <div className="min-w-0">
                        {address.label && (
                            <p className="font-medium text-sm text-base-content">
                                {address.label}
                            </p>
                        )}
                        <p className="text-base-content">
                            {formatAddressLine(address) || 'Endereço incompleto'}
                        </p>
                        {(address.state || address.zipCode) && (
                            <p className="text-sm text-base-content/60">
                                {[
                                    address.state,
                                    address.zipCode
                                        ? `CEP ${formatCEP(address.zipCode)}`
                                        : null,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </p>
                        )}
                    </div>
                </div>
            ))}
        </div>
    )
}
